import { useState, useEffect, useRef } from "react";
import { X, Search, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { clientsApi } from "./api";

/**
 * Compact client search + select.
 * props: value (object|null: {id, owner_name, phone, dog_name}), onChange(value)
 */
export function ClientPicker({ value, onChange, testid = "client-picker" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await clientsApi.list(q);
        setResults(res.data);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  useEffect(() => {
    const handler = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (value) {
    return (
      <div data-testid={testid} className="flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-[#E20074] bg-[#fdf3f8]">
        <div className="flex flex-col text-sm">
          <span className="font-medium text-[#0A0A0A]" data-testid={`${testid}-selected`}>{value.owner_name}</span>
          <span className="text-xs text-[#525252]">{[value.dog_name, value.phone].filter(Boolean).join(" · ") || "—"}</span>
        </div>
        <button type="button" onClick={() => onChange(null)} data-testid={`${testid}-clear`}
          className="text-[#525252] hover:text-[#E20074]" aria-label="Remover cliente">
          <X size={15} />
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={rootRef}>
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#525252] pointer-events-none" />
      <Input
        value={q}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        placeholder="Procurar cliente por nome, cão ou telefone…"
        className="pl-9"
        data-testid={`${testid}-input`}
      />
      {open && (
        <div className="absolute z-50 mt-1 left-0 right-0 bg-white border border-[#D4D4D4] rounded-md shadow-lg max-h-60 overflow-y-auto" data-testid={`${testid}-results`}>
          {loading ? (
            <div className="py-6 flex justify-center"><Loader2 size={16} className="animate-spin text-[#E20074]" /></div>
          ) : results.length === 0 ? (
            <p className="px-3 py-3 text-sm text-[#525252]">Sem clientes. Deixe em branco para marcação sem ficha.</p>
          ) : (
            results.slice(0, 20).map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => { onChange({ id: c.id, owner_name: c.owner_name, phone: c.phone, dog_name: c.dog_name }); setOpen(false); setQ(""); }}
                data-testid={`${testid}-option-${c.id}`}
                className="w-full text-left px-3 py-2 hover:bg-[#F4F4F0] flex flex-col"
              >
                <span className="text-sm font-medium">{c.owner_name}</span>
                <span className="text-xs text-[#525252]">{[c.dog_name, c.dog_breed, c.phone].filter(Boolean).join(" · ") || "—"}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
